/**
 * Listen History Service
 *
 * Handles API calls for user listen history.
 */

import api from './api';

export interface ListenHistoryEntry {
  userId: string;
  timestamp: number;
  streamId: string;
  streamName: string;
  streamDescription?: string;
  coverImage?: string;
  audioUrl?: string;
  uploaderUsername?: string;
  uploaderDisplayName?: string;
  duration?: number;
  progress?: number;
  completed: boolean;
  timeAgo: string;
  progressPercent?: number;
}

export interface ListenHistoryResponse {
  history: ListenHistoryEntry[];
  nextToken?: string;
}

export interface ListenStats {
  totalListens: number;
  uniqueStreams: number;
  completedListens: number;
  totalListenTime: number;
}

export const listenHistoryService = {
  /**
   * Get listen history (paginated)
   */
  async getHistory(limit: number = 20, cursor?: string): Promise<ListenHistoryResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) {
      params.append('cursor', cursor);
    }
    return api.get<ListenHistoryResponse>(`/listen-history?${params.toString()}`);
  },

  /**
   * Get recently played (deduplicated)
   */
  async getRecentlyPlayed(limit: number = 10): Promise<ListenHistoryResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      type: 'recent'
    });
    return api.get<ListenHistoryResponse>(`/listen-history?${params.toString()}`);
  },

  /**
   * Get continue listening (incomplete plays)
   */
  async getContinueListening(limit: number = 5): Promise<ListenHistoryResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      type: 'continue'
    });
    return api.get<ListenHistoryResponse>(`/listen-history?${params.toString()}`);
  },

  /**
   * Get listen statistics
   */
  async getStats(): Promise<ListenStats> {
    return api.get<ListenStats>('/listen-history?type=stats');
  },

  /**
   * Record a listen event
   */
  async recordListen(
    streamId: string,
    streamName: string,
    options?: {
      coverImage?: string;
      audioUrl?: string;
      uploaderUsername?: string;
      uploaderDisplayName?: string;
      duration?: number;
      progress?: number;
    }
  ): Promise<{ entry: ListenHistoryEntry }> {
    return api.post<{ entry: ListenHistoryEntry }>('/listen-history', {
      streamId,
      streamName,
      ...options
    });
  },

  /**
   * Clear all listen history
   */
  async clearHistory(): Promise<{ count: number }> {
    return api.delete<{ count: number }>('/listen-history');
  }
};

export default listenHistoryService;
